/**
 * Detecção automática de dispositivo (mobile/PWA × desktop). Não é
 * responsividade nem há alternância manual: o app detecta na carga e reavalia
 * quando o viewport/ponteiro muda, escolhendo UMA casca. `detectarDispositivo`
 * é pura (ambiente injetável) para teste.
 */
import { useEffect, useState } from "react";

export type Versao = "mobile" | "desktop";

const LARGURA_DESKTOP = 1024;
const UA_MOVEL = /Android|iPhone|iPad|iPod|Mobile|Tablet|Silk|Kindle|Opera Mini|IEMobile/i;

export interface AmbienteDispositivo {
  /** `(pointer: coarse)` — toque sem mouse preciso. */
  pointerCoarse: boolean;
  userAgent: string;
  innerWidth: number;
}

function lerAmbiente(): AmbienteDispositivo {
  if (typeof window === "undefined") {
    return { pointerCoarse: false, userAgent: "", innerWidth: LARGURA_DESKTOP };
  }
  return {
    pointerCoarse: window.matchMedia?.("(pointer: coarse)").matches ?? false,
    userAgent: window.navigator?.userAgent ?? "",
    innerWidth: window.innerWidth,
  };
}

/**
 * Desktop só quando há ponteiro preciso (mouse) E tela larga. Qualquer sinal de
 * celular/tablet (UA móvel, ponteiro grosso, ou viewport estreita) → mobile.
 */
export function detectarDispositivo(amb: AmbienteDispositivo = lerAmbiente()): Versao {
  if (UA_MOVEL.test(amb.userAgent)) {
    return "mobile";
  }
  if (amb.pointerCoarse) {
    return "mobile";
  }
  return amb.innerWidth >= LARGURA_DESKTOP ? "desktop" : "mobile";
}

/** Versão atual, automática: detecta e reavalia em resize/mudança de ponteiro. */
export function useVersao(): Versao {
  const [versao, setVersao] = useState<Versao>(detectarDispositivo);
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const reavaliar = () => setVersao(detectarDispositivo());
    const mq = window.matchMedia("(pointer: coarse)");
    window.addEventListener("resize", reavaliar);
    mq.addEventListener("change", reavaliar);
    reavaliar();
    return () => {
      window.removeEventListener("resize", reavaliar);
      mq.removeEventListener("change", reavaliar);
    };
  }, []);
  return versao;
}
